import { useState, useRef, useEffect, useCallback } from 'react';
import SortableResizableTable from './SortableResizableTable';
import TradeForm from './TradeForm';
import UnassignedCashView from './UnassignedCashView';
import OrderFormDialog from './OrderFormDialog';
import rrService from '../services/rrService';
import {
  getReportColumnsWithFormatting,
  formatCurrency as baseCurrencyFormatter,
  getTradeActionStyles as baseTradeActionStyles
} from '../config/tableConfig';
import CashSummaryPanel from './CashSummaryPanel';
import PortfolioRebalanceResultsModal from './PortfolioRebalanceResultsModal';

const RebalancePanel = ({
  // Display preferences
  hideDollarValues,

  // API and data dependencies
  selectedGroup,

  // Panel state from parent
  isRebalanceCollapsed
}) => {
  // Clear All Trades: removes all trades and collapses all rows
  const handleClearAllTrades = () => {
    setTradeFormData({});
    setExpandedTradeRows(new Set());
    setAccountCashLevels({});
    setIsLoadingCashLevels({});
  };
  // Local UI state management
  const [isRebalanceGuideCollapsed, setIsRebalanceGuideCollapsed] = useState(true);
  // Internal trade form state
  const [loadingSymbolsFor, setLoadingSymbolsFor] = useState({});

  // Internal rebalance data management
  const [isLoadingRebalance, setIsLoadingRebalance] = useState(false);
  const [rebalanceError, setRebalanceError] = useState('');
  const [rebalanceData, setRebalanceData] = useState(null);

  // Internal trade restrictions data management
  const [isLoadingTradeRestrictions, setIsLoadingTradeRestrictions] = useState(false);
  const [tradeRestrictionsError, setTradeRestrictionsError] = useState('');
  const [tradeRestrictionsData, setTradeRestrictionsData] = useState(null);

  // Internal cash actions data management
  const [isLoadingCashActions, setIsLoadingCashActions] = useState(false);
  const [cashActionsError, setCashActionsError] = useState('');
  const [cashActionsData, setCashActionsData] = useState(null);

  // Internal account mapping derived from data
  const [accountMapping, setAccountMapping] = useState({});
  const [availableAccounts, setAvailableAccounts] = useState([]);
  // Add custodian mapping state
  const [accountCustodianMapping, setAccountCustodianMapping] = useState({});

  // Internal state for trade management
  const [expandedTradeRows, setExpandedTradeRows] = useState(new Set());
  const [tradeFormData, setTradeFormData] = useState({});

  // Order form dialog state
  const [showOrderFormDialog, setShowOrderFormDialog] = useState(false);
  // Rebalance modal state
  const [showRebalanceModal, setShowRebalanceModal] = useState(false);
  const [rebalanceResult, setRebalanceResult] = useState(null);
  const [isLoadingPortfolioRebalance, setIsLoadingPortfolioRebalance] = useState(false);
  const [portfolioRebalanceError, setPortfolioRebalanceError] = useState('');
  const [rebalanceOperationType, setRebalanceOperationType] = useState('portfolio'); // 'portfolio' or 'cash_reserve'

  const [accountCashLevels, setAccountCashLevels] = useState({});
  const [isLoadingCashLevels, setIsLoadingCashLevels] = useState({});
  const [loadingCombinedTargets, setLoadingCombinedTargets] = useState({});
  const [localSecurityLevelModelSymbols, setLocalSecurityLevelModelSymbols] = useState({});

  // State for symbols for security level models for accounts
  const [symbolsForModelsAccounts, setSymbolsForModelsAccounts] = useState({});
  
  // State for UNASSIGNED CASH symbols by row ID
  const [unassignedCashSymbols, setUnassignedCashSymbols] = useState({});
  const [isLoadingUnassignedCashSymbols, setIsLoadingUnassignedCashSymbols] = useState({});
  
  // Refs for managing async operations
  const cashLevelsLoadingLock = useRef(new Set());
  const combinedTargetLoadingLock = useRef(new Set());
  const symbolsRef = useRef({});
  const lastFetchedRef = useRef(''); // Track last fetched group/file combination

  // Ref to track current symbols state for reliable access
  const cashLevelsAbortControllers = useRef(new Map());
  const combinedTargetAbortControllers = useRef(new Map());
  const cashLevelsDebounceTimers = useRef(new Map());
  const combinedTargetDebounceTimers = useRef(new Map());

  // Helper function to extract an array from potentially nested response data
  const extractDataArray = (response) => {
    // If response is already an array, use it
    if (Array.isArray(response)) {
      return response;
    }

    // If response has a data property that's an array, use it
    if (response && response.data && Array.isArray(response.data)) {
      return response.data;
    }

    // If response is an object, search recursively for the first array
    if (response && typeof response === 'object') {

      // First check top-level properties
      for (const key in response) {
        if (Array.isArray(response[key])) {
          return response[key];
        }
      }

      // Then check one level deeper
      for (const key in response) {
        if (response[key] && typeof response[key] === 'object') {
          for (const nestedKey in response[key]) {
            if (Array.isArray(response[key][nestedKey])) {
              return response[key][nestedKey];
            }
          }
        }
      }
    }

    // Default to empty array if no array found
    return [];
  };
  // Helper function to normalize field names for consistency
  const normalizeReportData = (data, reportType) => {
    if (!data || data.length === 0) return data;

    return data.map(item => {
      const normalized = { ...item };

      // Map common field variations to standard names
      Object.keys(item).forEach(key => {
        const lowerKey = key.toLowerCase();

        // Securities report specific normalizations
        if (reportType === 'securities') {
          if (lowerKey === 'symbol' || lowerKey === 'ticker') {
            normalized.symbol = item[key];
          }
        }
        // Trade restrictions specific normalizations
        if (reportType === 'tradeRestrictions') {
          if (lowerKey === 'symbol' || lowerKey === 'ticker') {
            normalized.Symbol = item[key];
          }
          if (lowerKey === 'restriction') {
            normalized.Restriction = item[key];
          }
        }

        // Cash actions report specific normalizations
        if (reportType === 'cashActions') {
          if (lowerKey.includes('account') && lowerKey.includes('name')) {
            normalized['Account Name'] = item[key];
          }
          if (lowerKey.includes('account') && lowerKey.includes('number')) {
            normalized['Account Number'] = item[key];
          }
          if (lowerKey === 'custodian') {
            normalized['Custodian'] = item[key];
          }
          if (lowerKey === 'cash action') {
            normalized['Cash Action'] = item[key];
          }
          if (lowerKey === 'holdings current value') {
            normalized['Holdings Current Value'] = item[key];
          }
          if (lowerKey === 'calculated reserve center') {
            normalized['Calculated Reserve Center'] = item[key];
          }
          if (lowerKey === 'calculated reserve min') {
            normalized['Calculated Reserve Min'] = item[key];
          }
          if (lowerKey === 'calculated reserve max') {
            normalized['Calculated Reserve Max'] = item[key];
          }
          // Keep legacy mappings for backward compatibility
          if (lowerKey.includes('cash') && lowerKey.includes('action') && lowerKey.includes('type')) {
            normalized['Cash Action Type'] = item[key];
          }
          if (lowerKey.includes('cash') && lowerKey.includes('amount')) {
            normalized['Cash Amount'] = item[key];
          }
          if (lowerKey.includes('cash') && lowerKey.includes('direction')) {
            normalized['Cash Direction'] = item[key];
          }
          if (lowerKey.includes('transaction') && lowerKey.includes('date')) {
            normalized['Transaction Date'] = item[key];
          }
          if (lowerKey.includes('cash') && lowerKey.includes('status')) {
            normalized['Cash Status'] = item[key];
          }
          if (lowerKey.includes('cash') && lowerKey.includes('note')) {
            normalized['Cash Notes'] = item[key];
          }
        }

        // Common field normalizations
        if (lowerKey.includes('sleeve') && lowerKey.includes('name')) {
          normalized.sleeveName = item[key];
        }
        if (lowerKey.includes('holdings') && lowerKey.includes('value')) {
          normalized.holdingsCurrentValue = item[key];
        }
        if (lowerKey.includes('target') && lowerKey.includes('weight')) {
          normalized.targetWeight = item[key];
        }
        if (lowerKey.includes('actual') && lowerKey.includes('weight') && !lowerKey.includes('assigned')) {
          normalized.actualWeight = item[key];
        }
        if (lowerKey.includes('actual') && lowerKey.includes('weight') && lowerKey.includes('assigned')) {
          normalized.actualWeightOfAssigned = item[key];
        }
      });

      return normalized;
    });
  };

  // Fetch rebalance data
  const fetchRebalanceData = async () => {
    if (!selectedGroup) return;

    setIsLoadingRebalance(true);
    setRebalanceError('');

    try {
      const response = await rrService.getGroupBySecuritiesReport(selectedGroup);
      // Handle the response data generically
      let data = extractDataArray(response);

      // Normalize data to ensure consistency in field names
      data = normalizeReportData(data, 'securities');

      setRebalanceData(data);
    } catch (err) {
      console.error('Error fetching rebalance data:', err);
      setRebalanceError(`Error loading rebalance: ${err.message || 'Unknown error'}`);
      setRebalanceData(null);
    } finally {
      setIsLoadingRebalance(false);
    }
  };

  // Fetch trade restrictions data
  const fetchTradeRestrictionsData = async () => {
    if (!selectedGroup) return;

    setIsLoadingTradeRestrictions(true);
    setTradeRestrictionsError('');

    try {
      const response = await rrService.getGroupTradeRestrictionsReport(selectedGroup);
      // Handle the response data generically
      let data = extractDataArray(response);

      // Normalize data to ensure consistency in field names
      data = normalizeReportData(data, 'tradeRestrictions');

      setTradeRestrictionsData(data);
    } catch (err) {
      console.error('Error fetching trade restrictions data:', err);
      setTradeRestrictionsError(`Error loading trade restrictions: ${err.message || 'Unknown error'}`);
      setTradeRestrictionsData(null);
    } finally {
      setIsLoadingTradeRestrictions(false);
    }
  };

  // Fetch cash actions data
  const fetchCashActionsData = async () => {
    if (!selectedGroup) return;

    setIsLoadingCashActions(true);
    setCashActionsError('');

    try {
      const response = await rrService.getGroupCashActionsReport(selectedGroup);

      // Handle the response data generically
      let data = extractDataArray(response);

      // Normalize data to ensure consistency in field names
      data = normalizeReportData(data, 'cashActions');

      setCashActionsData(data);
    } catch (err) {
      console.error('Error fetching cash actions data:', err);
      setCashActionsError(`Error loading cash actions: ${err.message || 'Unknown error'}`);
      setCashActionsData(null);
    } finally {
      setIsLoadingCashActions(false);
    }
  };
  
  // Portfolio rebalancing function
  const handlePortfolioRebalance = async () => {
    if (!selectedGroup) {
      setPortfolioRebalanceError('Please select a group and file first');
      return;
    }

    setIsLoadingPortfolioRebalance(true);
    setPortfolioRebalanceError('');
    setRebalanceResult(null);
    setRebalanceOperationType('portfolio');
    setShowRebalanceModal(true);

    try {
      // Configuration for the rebalancer - you can customize these values
      const config = {
        deviation_threshold: 0.005,    // 0.5% deviation threshold
        min_trade_value: 100.0,        // $100 minimum trade
        tax_efficiency_weight: 0.3,    // 30% weight on tax efficiency
        cash_buffer_percentage: 0.02   // 2% cash buffer
      };

      const result = await rrService.rebalancePortfolio(selectedGroup, config);
      setRebalanceResult(result);
    } catch (error) {
      console.error('Error during portfolio rebalancing:', error);
      setPortfolioRebalanceError(error.message || 'Failed to rebalance portfolio');
    } finally {
      setIsLoadingPortfolioRebalance(false);
    }
  };

  // Cash reserve rebalancing function
  const handleRaiseCash = async () => {
    if (!selectedGroup) {
      setPortfolioRebalanceError('Please select a group and file first');
      return;
    }

    setIsLoadingPortfolioRebalance(true);
    setPortfolioRebalanceError('');
    setRebalanceResult(null);
    setRebalanceOperationType('cash_reserve');
    setShowRebalanceModal(true);

    try {
      // Configuration for the cash reserve rebalancer
      const config = {
        deviation_threshold: 0.005,    // 0.5% deviation threshold
        min_trade_value: 100.0,        // $100 minimum trade
        tax_efficiency_weight: 0.3,    // 30% weight on tax efficiency
        cash_buffer_percentage: 0.02,  // 2% cash buffer
        rebalancer_type: 'cash_reserve' // Specify cash reserve rebalancer type
      };

      const result = await rrService.rebalancePortfolio(selectedGroup, config);
      setRebalanceResult(result);
    } catch (error) {
      console.error('Error during cash reserve rebalancing:', error);
      setPortfolioRebalanceError(error.message || 'Failed to raise cash');
    } finally {
      setIsLoadingPortfolioRebalance(false);
    }
  };

  // Spring cleaning rebalancing function
  const handleSpringCleaning = async () => {
    if (!selectedGroup) {
      setPortfolioRebalanceError('Please select a group and file first');
      return;
    }

    setIsLoadingPortfolioRebalance(true);
    setPortfolioRebalanceError('');
    setRebalanceResult(null);
    setRebalanceOperationType('spring_cleaning');
    setShowRebalanceModal(true);

    try {
      // Configuration for the spring cleaning rebalancer
      const config = {
        deviation_threshold: 0.005,    // 0.5% deviation threshold
        min_trade_value: 100.0,        // $100 minimum trade
        tax_efficiency_weight: 0.3,    // 30% weight on tax efficiency
        cash_buffer_percentage: 0.02,  // 2% cash buffer
        rebalancer_type: 'spring_cleaning', // Specify spring cleaning rebalancer type
        debug: false                   // Enable debug output if needed
      };

      const result = await rrService.rebalancePortfolio(selectedGroup, config);
      setRebalanceResult(result);
    } catch (error) {
      console.error('Error during spring cleaning rebalancing:', error);
      setPortfolioRebalanceError(error.message || 'Failed to perform spring cleaning rebalance');
    } finally {
      setIsLoadingPortfolioRebalance(false);
    }
  };

  // Fetch symbols for security level models for accounts
  const fetchSymbolsForModelsAccounts = useCallback(async (accountNumbers) => {
    if (!Array.isArray(accountNumbers) || accountNumbers.length === 0) return;
    try {
      const response = await rrService.getSymbolsForSecurityLevelModelsForAccounts(accountNumbers, { });
      if (response && response.result) {
        setSymbolsForModelsAccounts(response.result);
      }
    } catch (error) {
      console.error('Error fetching symbols for models/accounts:', error);
    }
  }, []);

  // Effect to fetch rebalance data when dependencies change
  useEffect(() => {
    if (!isRebalanceCollapsed && selectedGroup) {
      fetchRebalanceData();
      fetchTradeRestrictionsData();
      fetchCashActionsData();
    }
  }, [isRebalanceCollapsed, selectedGroup]);

  // Effect to extract account mapping from data
  useEffect(() => {
    if (rebalanceData && Array.isArray(rebalanceData)) {
      // Create account mapping from account names to account numbers
      const accountMap = {};
      const accountNames = [...new Set(rebalanceData.flatMap(item => {
        const accountNames = item['Account Names'] || item.accountNames || '';
        const accountNumbers = item['Account Numbers'] || item.accountNumbers || '';

        // Handle both string and array formats for account names and numbers
        let namesArray = [];
        let numbersArray = [];

        if (typeof accountNames === 'string') {
          namesArray = accountNames.split(',').map(s => String(s.trim())).filter(Boolean);
        } else if (Array.isArray(accountNames)) {
          namesArray = accountNames.map(name => String(name)).filter(Boolean);
        }

        if (typeof accountNumbers === 'string') {
          numbersArray = accountNumbers.split(',').map(s => String(s.trim())).filter(Boolean);
        } else if (Array.isArray(accountNumbers)) {
          numbersArray = accountNumbers.map(num => String(num)).filter(Boolean);
        }

        // Map account names to account numbers from rebalanceData
        namesArray.forEach((name, index) => {
          const number = numbersArray[index] || numbersArray[0]; // Use first number if mismatch
          if (name && number) {
            accountMap[String(name)] = String(number);
          }
        });

        return namesArray;
      }))];

      // Enhanced: Override with cashActionsData if available for consistency with cash warnings
      if (cashActionsData && Array.isArray(cashActionsData)) {
        cashActionsData.forEach(account => {
          const name = account['Account Name'] || account.accountName;
          const number = account['Account Number'] || account.accountNumber;

          if (name && number) {
            const nameStr = String(name).trim();
            const numberStr = String(number).trim();
            accountMap[nameStr] = numberStr;
          }
        });
      }

      setAccountMapping(accountMap);
      setAvailableAccounts(accountNames);
    }
    // Create custodian mapping from cashActionsData which has the most reliable account-to-custodian data
    if (cashActionsData && Array.isArray(cashActionsData)) {
      const custodianMap = {};
      cashActionsData.forEach(account => {
        const accountNumber = account['Account Number'] || account.accountNumber;
        const custodian = account['Custodian'] || account.custodian;

        if (accountNumber && custodian) {
          const numberStr = String(accountNumber).trim();
          const custodianStr = String(custodian).trim();
          custodianMap[numberStr] = custodianStr;
        }
      });
      setAccountCustodianMapping(custodianMap);
    }
  }, [rebalanceData, cashActionsData]);

  // Reset warned accounts when data changes to avoid outdated warnings
  useEffect(() => {
    warnedAccountsRef.current.clear();
  }, [rebalanceData, cashActionsData, accountMapping, accountCustodianMapping]);

  // Keep the symbols ref in sync with the state for reliable access
  useEffect(() => {
    symbolsRef.current = localSecurityLevelModelSymbols;
  }, [localSecurityLevelModelSymbols]);

  // Fetch symbols for all accounts/models ONLY when rebalanceData changes and is up-to-date for the current group/file
  useEffect(() => {
    if (!selectedGroup || isRebalanceCollapsed) return;

    // Create a unique key for this group/file combination
    const currentKey = `${selectedGroup}`;

    // Only proceed if we haven't fetched for this combination yet
    if (lastFetchedRef.current === currentKey) {
      return;
    }

    let accountNumbers = [];
    if (rebalanceData && Array.isArray(rebalanceData) && rebalanceData.length > 0) {
      // fallback: extract from rebalanceData
      const accountMap = {};
      rebalanceData.forEach(item => {
        const accountNumbersField = item['Account Numbers'] || item.accountNumbers || '';
        let numbersArray = [];
        if (typeof accountNumbersField === 'string') {
          numbersArray = accountNumbersField.split(',').map(s => String(s.trim())).filter(Boolean);
        } else if (Array.isArray(accountNumbersField)) {
          numbersArray = accountNumbersField.map(num => String(num)).filter(Boolean);
        }
        numbersArray.forEach(number => {
          if (number) accountMap[number] = true;
        });
      });
      accountNumbers = Object.keys(accountMap);
    }

    if (accountNumbers.length > 0) {
      fetchSymbolsForModelsAccounts(accountNumbers);
      lastFetchedRef.current = currentKey;
    }
  }, [rebalanceData, fetchSymbolsForModelsAccounts]);
  
  // Reset the fetch tracking when group or file changes
  useEffect(() => {
    lastFetchedRef.current = '';
  }, [selectedGroup]);

  // Clear trade form data when group or file changes to prevent leftover data
  useEffect(() => {
    // Clear trade form data and collapse all expanded rows
    setTradeFormData({});
    setExpandedTradeRows(new Set());
    
    // Also clear account cash levels since they're tied to specific trades
    setAccountCashLevels({});
    setIsLoadingCashLevels({});
    
    // Clear any loading states
    setLoadingCombinedTargets({});
    
    // Clear UNASSIGNED CASH symbols state
    setUnassignedCashSymbols({});
    setIsLoadingUnassignedCashSymbols({});
    
    // Clear abort controllers and timers to prevent stale API calls
    cashLevelsAbortControllers.current.forEach(controller => controller.abort());
    cashLevelsAbortControllers.current.clear();
    combinedTargetAbortControllers.current.forEach(controller => controller.abort());
    combinedTargetAbortControllers.current.clear();
    
    cashLevelsDebounceTimers.current.forEach(timer => clearTimeout(timer));
    cashLevelsDebounceTimers.current.clear();
    combinedTargetDebounceTimers.current.forEach(timer => clearTimeout(timer));
    combinedTargetDebounceTimers.current.clear();
    
    // Clear loading locks
    cashLevelsLoadingLock.current.clear();
    combinedTargetLoadingLock.current.clear();
  }, [selectedGroup]);

  // Initial load effect - check if we need to fetch symbols when panel opens with data ready
  useEffect(() => {
    if (!selectedGroup || isRebalanceCollapsed) return;

    // Create a unique key for this group/file combination
    const currentKey = `${selectedGroup}`;

    // Only proceed if we haven't fetched for this combination yet
    if (lastFetchedRef.current === currentKey) {
      return;
    }

    // Check if we have account data available for immediate processing
    let accountNumbers = [];

    if (rebalanceData && Array.isArray(rebalanceData)) {
      const accountMap = {};
      rebalanceData.forEach(item => {
        const accountNumbersField = item['Account Numbers'] || item.accountNumbers || '';
        let numbersArray = [];
        if (typeof accountNumbersField === 'string') {
          numbersArray = accountNumbersField.split(',').map(s => String(s.trim())).filter(Boolean);
        } else if (Array.isArray(accountNumbersField)) {
          numbersArray = accountNumbersField.map(num => String(num)).filter(Boolean);
        }
        numbersArray.forEach(number => {
          if (number) accountMap[number] = true;
        });
      });
      accountNumbers = Object.keys(accountMap);
    }
    // Fallback: use cashActionsData
    else if (cashActionsData && Array.isArray(cashActionsData)) {
      accountNumbers = cashActionsData.map(account =>
        String(account['Account Number'] || account.accountNumber || '').trim()
      ).filter(Boolean);
    }

    if (accountNumbers && accountNumbers.length > 0) {
      fetchSymbolsForModelsAccounts(accountNumbers);
      lastFetchedRef.current = currentKey;
    }
  }, [rebalanceData, fetchSymbolsForModelsAccounts]);

  // Internal table configuration functions
  const formatCurrency = useCallback((value) => {
    return baseCurrencyFormatter(value, hideDollarValues);
  }, [hideDollarValues]);

  // Custom styling function for trade action badges (use base styles)
  const getTradeActionStyles = baseTradeActionStyles;

  // Get column definitions with proper formatting context
  const getColumnsForReport = (reportType) => {
    return getReportColumnsWithFormatting(reportType, hideDollarValues);
  };
  // Generate specific column arrays that update when hideDollarValues changes
  const securitiesReportColumns = getColumnsForReport('securities');

  // Simplified table configuration for RebalancePanel (securities-focused)
  const getTableConfig = (data) => {
    if (!data || data.length === 0) {
      return { columns: securitiesReportColumns };
    }

    // Create a function to format value display for securities report
    const renderCell = (value, item, columnKey) => {
      if (value === undefined || value === null) return '';

      // Special handling for Trade Action With Restriction status
      if (columnKey === 'Trade Action With Restriction' || columnKey === 'Trade Action With Restriction (of Assigned)') {
        if (!value || value === '') {
          return <span className="text-muted" style={{ fontSize: '0.9rem' }}>-</span>;
        }

        // Convert to string and check for action keywords
        const actionText = String(value);
        const upperText = actionText.toUpperCase();

        let customClass = '';

        // Check for primary action keywords (BUY, SELL, HOLD)
        if (upperText === 'BUY') {
          customClass = 'trade-action-buy';
        } else if (upperText === 'SELL') {
          customClass = 'trade-action-sell';
        } else if (upperText === 'HOLD') {
          customClass = 'trade-action-hold';
        } else if (upperText.includes('BUY') && upperText.includes('WITHIN WEIGHT')) {
          customClass = 'trade-action-buy-weight';
        } else if (upperText.includes('SELL') && upperText.includes('WITHIN WEIGHT')) {
          customClass = 'trade-action-sell-weight';
        } else if (upperText.includes('BUT')) {
          customClass = 'trade-action-conflict';
        } else {
          customClass = 'trade-action-unknown';
        }

        const customStyle = {
          fontSize: '0.9rem',
          padding: '5px 8px',
          borderRadius: '4px',
          fontWeight: '500',
          display: 'inline-block',
          ...getTradeActionStyles(customClass)
        };        return <span className={customClass} style={customStyle} title={actionText}>{actionText}</span>;
      }

      // Special handling for Security Level Model Name - visual indication for unassigned models
      if (columnKey === 'Security Level Model Name') {
        if (isModelUnassignedCash(value)) {
          // Special styling for UNASSIGNED CASH - clickable like other expandable models
          const displayValue = typeof value === 'object' && value?.value 
            ? String(value.value) 
            : String(value || '');
            
          return (
            <span 
              style={{ cursor: 'pointer', textDecoration: 'underline', color: '#28a745', fontWeight: '600' }}
              title="Click to expand to view cash holdings details"
            >
              {displayValue}
            </span>
          );
        } else if (isModelUnassigned(value)) {
          // Add visual styling to indicate this row cannot be expanded for trading
          const unassignedStyle = {
            color: '#6c757d',
            fontStyle: 'italic',
            cursor: 'not-allowed'
          };
          
          const displayValue = typeof value === 'object' && value?.value 
            ? String(value.value) 
            : String(value || '');
            
          return (
            <span 
              style={unassignedStyle} 
              title="Trading not available for unassigned models"
            >
              {displayValue}
            </span>
          );
        }
        
        // For assigned models, show normal clickable styling
        const displayValue = typeof value === 'object' && value?.value 
          ? String(value.value) 
          : String(value || '');
          
        return (
          <span 
            style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
            title="Click to expand for trading options"
          >
            {displayValue}
          </span>
        );
      }

      // Color coding for weight comparison in Reports table
      if (item['Target Weight'] !== undefined &&
        (columnKey === 'Actual Weight' || columnKey === 'Actual Weight (of Assigned)')) {
        const targetWeight = item['Target Weight'];
        let tooltipText = '';
        let weightComparisonClass = '';

        if ((columnKey === 'Actual Weight' || columnKey === 'Actual Weight (of Assigned)') && value !== undefined) {
          const minTargetWeight = item['Minimum Target Weight'] || item['minimumTargetWeight'] || targetWeight;
          const maxTargetWeight = item['Maximum Target Weight'] || item['maximumTargetWeight'] || targetWeight;

          const difference = value - targetWeight;
          if (value > maxTargetWeight) {
            weightComparisonClass = 'text-sell fw-bold';
            tooltipText = `Above maximum target (${maxTargetWeight}%)`;

            return (
              <>
                <span className={weightComparisonClass} title={tooltipText}>
                  {typeof value === 'number' ? `${value.toFixed(2)}%` : value} <span>({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                </span>
              </>
            );
          } else if (value < minTargetWeight) {
            weightComparisonClass = 'text-buy fw-bold';
            tooltipText = `Below minimum target (${minTargetWeight}%)`;

            return (
              <>
                <span className={weightComparisonClass} title={tooltipText}>
                  {typeof value === 'number' ? `${value.toFixed(2)}%` : value} <span>({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                </span>
              </>
            );
          } else {
            tooltipText = `Within target range (${minTargetWeight}% - ${maxTargetWeight}%)`;

            return (
              <>
                <span title={tooltipText}>
                  {typeof value === 'number' ? `${value.toFixed(2)}%` : value} <span>({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                </span>
              </>
            );
          }
        }
      }

      // Format the value for display and add tooltip to show full value
      let displayValue;
      let tooltipText = '';

      // Handle currency formatting based on field name patterns
      if (typeof value === 'number') {
        const fieldLower = columnKey.toLowerCase();
        const isCurrencyField = fieldLower.includes('value') ||
          fieldLower.includes('price') ||
          fieldLower.includes('amount') ||
          fieldLower.includes('cash') ||
          fieldLower.includes('cost') ||
          fieldLower.includes('fee');

        const isPercentageField = fieldLower.includes('weight') ||
          fieldLower.includes('percent') ||
          fieldLower.includes('rate');

        if (isCurrencyField) {
          displayValue = formatCurrency(value);
          tooltipText = `Exact value: ${formatCurrency(value)}`;
        } else if (isPercentageField) {
          displayValue = `${value.toFixed(2)}%`;
          tooltipText = `Exact value: ${value.toFixed(4)}%`;
        } else {
          // For other numeric fields, format with appropriate precision
          if (value % 1 === 0) {
            displayValue = value.toString();
          } else {
            displayValue = value.toFixed(2);
          }
          tooltipText = `Exact value: ${value}`;
        }
      } else {
        displayValue = String(value);
        tooltipText = String(value);
      }

      return <span title={tooltipText}>{displayValue}</span>;
    };

    // Return custom render function to handle various field formats
    return {
      customRender: {
        renderCell
      }
    };
  };

  // Helper function to generate consistent row ID based on Security Level Model Name
  const generateRowId = (item, fallbackIndex = null) => {
    if (!item) return fallbackIndex !== null ? `row-${fallbackIndex}` : 'unknown';
    
    const modelName = item['Security Level Model Name'];
    let modelString = '';
    
    // Handle both object format and string format
    if (typeof modelName === 'object' && modelName?.value) {
      modelString = String(modelName.value);
    } else if (modelName !== undefined && modelName !== null) {
      modelString = String(modelName);
    }
    
    // Clean up the model string to make it a valid identifier
    if (modelString && modelString.trim() !== '') {
      // Remove special characters and spaces, replace with underscores
      const cleanModelString = modelString.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').toLowerCase();
      const rowId = `model_${cleanModelString}`;
      return rowId;
    }
    
    // Fallback to symbol if model name is not available
    const symbol = item.Symbol || item.symbol;
    if (symbol) {
      const cleanSymbol = String(symbol).replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').toLowerCase();
      const rowId = `symbol_${cleanSymbol}`;
      return rowId;
    }
    
    // Final fallback
    const rowId = fallbackIndex !== null ? `row_${fallbackIndex}` : 'unknown';
    return rowId;
  };

  // Helper function to check if a security level model is unassigned
  const isModelUnassigned = (modelName) => {
    if (!modelName) return false;
    
    // Handle both object format and string format
    const modelString = typeof modelName === 'object' && modelName?.value
      ? String(modelName.value)
      : String(modelName || '');
    
    return modelString.toLowerCase().includes('unassigned');
  };

  // Helper function to check if a security level model is "UNASSIGNED CASH"
  const isModelUnassignedCash = (modelName) => {
    if (!modelName) return false;
    
    // Handle both object format and string format
    const modelString = typeof modelName === 'object' && modelName?.value
      ? String(modelName.value)
      : String(modelName || '');
    
    return modelString.toLowerCase() === 'unassigned cash';
  };

  // Helper function to check if a model should be expandable
  const isModelExpandable = (modelName) => {
    return !isModelUnassigned(modelName) || isModelUnassignedCash(modelName);
  };
    // Handle row expansion and load symbols for all accounts
  const handleRowExpand = (item, originalIndex, isExpanded, rowId) => {
    // Check if trying to expand a row with non-expandable model
    if (isExpanded && item) {
      const modelName = item['Security Level Model Name'];
      
      if (!isModelExpandable(modelName)) {
        // Don't allow expansion for non-expandable models
        return;
      }
    }

    // Update the expanded state using stable row ID
    setExpandedTradeRows(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
        // Note: We don't clear form data when collapsing to preserve applied trades
      }
      return newSet;
    });

    // If collapsing, we can return early here after state update
    if (!isExpanded) {
      return;
    }
    if (isExpanded && item) {
      const modelName = item['Security Level Model Name'];

      // Check if this is an UNASSIGNED CASH model
      if (isModelUnassignedCash(modelName)) {
        return; // Exit early for UNASSIGNED CASH
      }

      // Initialize form data for this row if not already present using stable rowId
      setTradeFormData(prevData => {
        if (!prevData[rowId]) {
          return {
            ...prevData,
            [rowId]: {
              trades: [{
                symbol: String(item.Symbol || ''),
                account: '',
                action: 'Buy',
                quantity: 0,
                price: 0.0,
                dollars: 0,
                currentHoldings: 0
              }],
              resultingTarget: item['Target Weight'] || 0
            }
          };
        }
        return prevData;
      });

      // Call API immediately with empty trades to get baseline combined resulting weight
      setTimeout(async () => {
        try {
          const securityLevelModelString = typeof modelName === 'object' && modelName?.value
            ? String(modelName.value)
            : String(modelName || '');

          if (securityLevelModelString && selectedGroup) {
            const response = await rrService.calculateCombinedResultingTarget(
              selectedGroup,
              securityLevelModelString,
              [], // Empty trades array for baseline calculation
              {}
            );

            if (response && response.combined_resulting_target !== undefined) {
              setTradeFormData(prevData => ({
                ...prevData,
                [rowId]: {
                  ...(prevData[rowId] || {}),
                  resultingTarget: response.combined_resulting_target
                }
              }));
            }
          }
        } catch (error) {
          console.error('Error calculating baseline combined resulting weight:', error);
        }
      }, 100);      // Load symbols for all accounts when row is expanded
      // Note: Symbols are already preloaded via symbolsForModelsAccounts, no need to load individually
    }
  };

  // Trade form handling functions
  const handleSecurityLevelModelClick = (item, rowIndex) => {
    const modelName = item['Security Level Model Name'];
    
    // Don't allow expansion for non-expandable models
    if (!isModelExpandable(modelName)) {
      return;
    }

    // Create stable row identifier based on Security Level Model Name
    const rowId = generateRowId(item, rowIndex);

    setExpandedTradeRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
        // Don't clear trade data when collapsing - trades should persist
      } else {
        newSet.add(rowId);
        
        // Check if this is an UNASSIGNED CASH model
        if (isModelUnassignedCash(modelName)) {
          // Fill in later
        } else {
          // Initialize form data for this row with multiple trades support
          setTradeFormData(prevData => ({
            ...prevData,
            [rowId]: {
              trades: [{
                symbol: String(item.Symbol || ''),
                account: '',
                action: 'Buy',
                quantity: 0,
                price: 0.0,
                dollars: 0,
                currentHoldings: 0
              }],
              resultingTarget: item['Target Weight'] || 0
            }
          }));

          // Call API immediately with empty trades to get baseline combined resulting weight
          setTimeout(async () => {
            try {
              const modelName = item['Security Level Model Name'];

              const securityLevelModelString = typeof modelName === 'object' && modelName?.value
                ? String(modelName.value)
                : String(modelName || '');

              if (securityLevelModelString && selectedGroup) {
                const response = await rrService.calculateCombinedResultingTarget(
                  selectedGroup,
                  securityLevelModelString,
                  [], // Empty trades array for baseline calculation
                  {}
                );              if (response && response.combined_resulting_target !== undefined) {
                  setTradeFormData(prevData => ({
                    ...prevData,
                    [rowId]: {
                      ...(prevData[rowId] || {}),
                      resultingTarget: response.combined_resulting_target
                    }
                  }));
                }
              }
            } catch (error) {
              console.error('[EXPANSION] Error calling baseline API:', error);
            }
          }, 100);

          // Note: Symbols will be available via symbolsForModelsAccounts (already preloaded)
        }
      }
      return newSet;
    });
  };

  // Internal function: Get restriction for a symbol from trade restrictions data
  // Track warned accounts to prevent console spam
  const warnedAccountsRef = useRef(new Set());

  // Helper function to get restriction for a symbol from trade restrictions data
  const getSymbolRestriction = useCallback((symbol) => {
    if (!tradeRestrictionsData || !Array.isArray(tradeRestrictionsData) || !symbol) {
      return null;
    }

    const restrictionEntry = tradeRestrictionsData.find(entry =>
      entry.Symbol === symbol
    );

    if (restrictionEntry) {
      // Try different possible field names for the restriction
      const restriction = restrictionEntry.Restriction ||
        restrictionEntry['Security Restriction Type'] ||
        restrictionEntry.RestrictionType ||
        restrictionEntry.restriction;

      if (restriction && restriction.trim() !== '') {
        const cleanRestriction = restriction.replace(/\s*\([ASB]\)/g, '');
        return cleanRestriction;
      }
    }
    return null;
  }, [tradeRestrictionsData]);

  // Helper function to collect all trades from the form data
  const collectAllTrades = useCallback(() => {
    const allTrades = [];

    Object.entries(tradeFormData).forEach(([rowIndex, formData]) => {
      if (formData && formData.trades) {
        formData.trades.forEach((trade, tradeIndex) => {
          if (trade.symbol && trade.account && trade.quantity !== 0) {
            // Find the item in rebalanceData that matches this rowIndex (now rowId)
            const rowData = rebalanceData?.find(dataItem => {
              const itemRowId = dataItem.Symbol || dataItem.symbol || dataItem.id || `row-${rebalanceData.indexOf(dataItem)}`;
              return itemRowId === rowIndex;
            });
            // Get custodian for this account using the reliable mapping from cashActionsData
            const accountNumber = accountMapping[trade.account];
            // const custodian = accountCustodianMapping[accountNumber] || 'Unknown';            // Filter out custodians that aren't supported for trading
            // const validCustodians = ['fidelity', 'charles schwab', 'empower'];
            // const isValidCustodian = validCustodians.some(valid =>
            //   custodian.toLowerCase().includes(valid)
            // );

            // if (!isValidCustodian) {
            //   // Only warn once per account to prevent console spam
            //   if (!warnedAccountsRef.current.has(trade.account)) {
            //     console.warn(`Skipping trade for account ${trade.account} with unsupported custodian: ${custodian}`);
            //     warnedAccountsRef.current.add(trade.account);
            //   }
            //   return; // Skip this trade if custodian is not valid
            // }
            allTrades.push({
              ...trade,
              rowIndex: rowIndex,
              tradeIndex,
              symbol: trade.symbol,
              account: trade.account,
              accountNumber: accountNumber,
              action: trade.action,
              quantity: trade.quantity,
              // Add additional context from the row data
              securityName: rowData?.['Security Name'] || rowData?.securityName || trade.symbol,
              sleeveName: rowData?.sleeveName || rowData?.['Sleeve Name'] || '',
              // Add security type information from Trading Security Type column
              securityType: rowData?.['Trading Security Type'] || 'EQUITY',
              // Add restriction information
              restriction: getSymbolRestriction(trade.symbol)
            });
          }
        });
      }
    });

    return allTrades;
  }, [tradeFormData, accountMapping, accountCustodianMapping, rebalanceData, tradeRestrictionsData, getSymbolRestriction]);

  // Helper function to analyze trades for restrictions and cash issues
  const analyzeTradesForIssues = useCallback((trades) => {
    const issues = [];
    const warnings = [];

    trades.forEach((trade, index) => {
      // Check for restriction violations
      if (trade.restriction) {
        const restrictionUpper = trade.restriction.toUpperCase();
        let hasRestrictionViolation = false;
        let violationMessage = '';

        if (restrictionUpper.includes('HOLD') && trade.quantity !== 0) {
          hasRestrictionViolation = true;
          violationMessage = `Trade violates HOLD restriction: ${trade.restriction}`;
        } else if (restrictionUpper.includes('BUY') && trade.quantity < 0) {
          hasRestrictionViolation = true;
          violationMessage = `SELL trade violates BUY restriction: ${trade.restriction}`;
        } else if (restrictionUpper.includes('SELL') && trade.quantity > 0) {
          hasRestrictionViolation = true;
          violationMessage = `BUY trade violates SELL restriction: ${trade.restriction}`;
        }

        if (hasRestrictionViolation) {
          issues.push({
            type: 'restriction',
            trade,
            tradeIndex: index,
            message: violationMessage,
            severity: 'error'
          });
        }
      }

      // Check for cash issues
      const accountNumber = accountMapping[trade.account];
      if (accountNumber && accountCashLevels[accountNumber]) {
        const cashInfo = accountCashLevels[accountNumber];
        const { new_cash_level, min_reserve, max_reserve } = cashInfo;

        // Check for negative cash
        if (new_cash_level < 0) {
          issues.push({
            type: 'cash',
            trade,
            tradeIndex: index,
            message: `Trade would result in negative cash balance: ${formatCurrency(new_cash_level)}`,
            severity: 'error'
          });
        }
        // Check for cash below minimum reserve
        else if (new_cash_level < min_reserve) {
          warnings.push({
            type: 'cash',
            trade,
            tradeIndex: index,
            message: `Cash balance ${formatCurrency(new_cash_level)} would be below minimum reserve ${formatCurrency(min_reserve)}`,
            severity: 'warning'
          });
        }
        // Check for cash above maximum reserve (informational)
        else if (new_cash_level > max_reserve && max_reserve > 0) {
          warnings.push({
            type: 'cash',
            trade,
            tradeIndex: index,
            message: `Cash balance ${formatCurrency(new_cash_level)} would be above maximum reserve ${formatCurrency(max_reserve)}`,
            severity: 'info'
          });
        }
      }
    });
    return { issues, warnings };
  }, [accountMapping, accountCashLevels, formatCurrency]);

  // Internal function: Calculate combined resulting weight with trades (bulk version)
  const calculateCombinedResultingWeightBulk = async (securityModelTrades) => {
    try {
      if (!securityModelTrades || securityModelTrades.length === 0) {
        return;
      }

      // Group trades by security model
      const securityModels = securityModelTrades.map(item => ({
        security_level_model: item.securityLevelModel,
        trades: item.trades.map(trade => {
          const accountNumber = accountMapping[trade.account];
          return {
            account_number: accountNumber,
            symbol: trade.symbol,
            quantity: trade.quantity,
            price: trade.price
          };
        }).filter(trade => trade.account_number !== undefined && trade.account_number !== null && trade.account_number !== '')
      })).filter(item => item.trades.length > 0);

      if (securityModels.length === 0) {
        return;
      }

      // Call the bulk API
      const response = await rrService.calculateCombinedResultingTargetBulk(
        selectedGroup,
        securityModels,
        {}
      );

      if (response && response.results) {
        // Update trade form data with the results
        setTradeFormData(prevData => {
          const newData = { ...prevData };
          
          // Map results back to row indexes
          securityModelTrades.forEach((item, index) => {
            const result = response.results.find(r => r.security_level_model === item.securityLevelModel);

            if (result && result.combined_resulting_target !== undefined) {
              newData[item.rowIndex] = {
                ...(newData[item.rowIndex] || {}),
                resultingTarget: result.combined_resulting_target
              };
            }
          });

          return newData;
        });
      }
    } catch (error) {
      console.error('Error in calculateCombinedResultingWeightBulk:', error);
      // Fallback to individual calls if bulk fails
      securityModelTrades.forEach(item => {
        calculateCombinedResultingWeightWithTrades(item.rowIndex, item.trades);
      });
    }
  };

  // Internal function: Calculate combined resulting weight with trades
  const calculateCombinedResultingWeightWithTrades = (rowKey, trades, options = {}) => {
    const loadingKey = `${rowKey}_combined_target`;
    const DEBOUNCE_DELAY = 350;

    if (combinedTargetDebounceTimers.current.has(loadingKey)) {
      clearTimeout(combinedTargetDebounceTimers.current.get(loadingKey));
    }

    const timerId = setTimeout(async () => {
      if (combinedTargetLoadingLock.current.has(loadingKey)) {
        return;
      }

      if (combinedTargetAbortControllers.current.has(loadingKey)) {
        combinedTargetAbortControllers.current.get(loadingKey).abort();
        combinedTargetAbortControllers.current.delete(loadingKey);
      }

      const abortController = new AbortController();
      combinedTargetAbortControllers.current.set(loadingKey, abortController);
      combinedTargetLoadingLock.current.add(loadingKey);
      setLoadingCombinedTargets(prev => ({ ...prev, [loadingKey]: true }));

      try {
        let securityLevelModelString = undefined;
        if (rebalanceData) {
          // Find the item in rebalanceData that matches this rowKey (now rowId)
          const item = rebalanceData.find(dataItem => {
            const itemRowId = generateRowId(dataItem, rebalanceData.indexOf(dataItem));
            return itemRowId === rowKey;
          });
          
          if (item) {
            const modelNameData = item['Security Level Model Name'];
            if (typeof modelNameData === 'object' && modelNameData !== null && modelNameData.value !== undefined) {
              securityLevelModelString = String(modelNameData.value);
            } else if (modelNameData !== undefined && modelNameData !== null) {
              securityLevelModelString = String(modelNameData);
            }
          }
        }

        if (!securityLevelModelString || typeof securityLevelModelString !== 'string' || securityLevelModelString.toLowerCase() === '[object object]' || securityLevelModelString.trim() === '') {
          console.error(`Invalid securityLevelModel for rowKey=${rowKey}`);
          setLoadingCombinedTargets(prev => {
            const newState = { ...prev };
            delete newState[loadingKey];
            return newState;
          });
          combinedTargetLoadingLock.current.delete(loadingKey);
          combinedTargetAbortControllers.current.delete(loadingKey);
          return;
        }

        if (!Array.isArray(trades)) {
          console.error(`'trades' is not an array for rowKey=${rowKey}:`, trades);
          setLoadingCombinedTargets(prev => {
            const newState = { ...prev };
            delete newState[loadingKey];
            return newState;
          });
          combinedTargetLoadingLock.current.delete(loadingKey);
          combinedTargetAbortControllers.current.delete(loadingKey);
          return;
        }

        const formattedTrades = trades.map(trade => {
          const accountNumber = accountMapping[trade.account];
          return {
            account_number: accountNumber,
            symbol: trade.symbol,
            quantity: trade.quantity,
            price: trade.price
          };
        })
          .filter(trade => trade.account_number !== undefined && trade.account_number !== null && trade.account_number !== '');
        // If no valid trades remain, calculate with empty trades to get the current weight
        if (formattedTrades.length === 0) {
          // Calculate resulting weight with no trades (which should return current actual weight)
          try {
            const response = await rrService.calculateCombinedResultingTarget(
              selectedGroup,
              securityLevelModelString,
              [], // Empty trades array
              {
                signal: abortController.signal
              }
            );

            if (response && response.combined_resulting_target !== undefined) {
              setTradeFormData(prevData => ({
                ...prevData,
                [rowKey]: {
                  ...(prevData[rowKey] || {}),
                  resultingTarget: response.combined_resulting_target
                }
              }));
            }
          } catch (error) {
            console.error(`Error calculating weight with no trades for ${rowKey}:`, error);
            // Fallback to current actual weight
            setTradeFormData(prevData => {
              // Find the item in rebalanceData that matches this rowKey (now rowId)
              const item = rebalanceData?.find(dataItem => {
                const itemRowId = dataItem.Symbol || dataItem.symbol || dataItem.id || `row-${rebalanceData.indexOf(dataItem)}`;
                return itemRowId === rowKey;
              });
              
              return {
                ...prevData,
                [rowKey]: {
                  ...(prevData[rowKey] || {}),
                  resultingTarget: item ?
                    (item['Actual Weight (of Assigned)'] ||
                      item['Actual Weight'] ||
                      item['actualWeight'] || 0) : 0
                }
              };
            });
          }
          return;
        }

        const response = await rrService.calculateCombinedResultingTarget(
          selectedGroup,
          securityLevelModelString,
          formattedTrades,
          {
            signal: abortController.signal
          }
        );
        if (response && response.combined_resulting_target !== undefined) {
          setTradeFormData(prevData => {
            const newData = {
              ...prevData,
              [rowKey]: {
                ...(prevData[rowKey] || {}),
                resultingTarget: response.combined_resulting_target
              }
            };
            return newData;
          });
        } else {
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`Error in calculateCombinedResultingWeightWithTrades for ${loadingKey}:`, error);
        }
      } finally {
        setLoadingCombinedTargets(prev => {
          const newState = { ...prev };
          delete newState[loadingKey];
          return newState;
        });
        combinedTargetLoadingLock.current.delete(loadingKey);
        combinedTargetAbortControllers.current.delete(loadingKey);
      }
    }, DEBOUNCE_DELAY);

    combinedTargetDebounceTimers.current.set(loadingKey, timerId);
  };

  // Function to initialize trade form data for a specific row  // Add a new trade row to the trades array
  const addTradeRow = (rowIndex) => {
    setTradeFormData(prevData => {
      const currentRowData = prevData[rowIndex];
      
      // Find the item in rebalanceData that matches this rowIndex (now rowId)
      const item = rebalanceData.find(dataItem => {
        const itemRowId = generateRowId(dataItem, rebalanceData.indexOf(dataItem));
        return itemRowId === rowIndex;
      });
      
      const symbolName = String(item?.Symbol || '');

      // Create a new trade object
      const newTrade = {
        symbol: symbolName,
        account: '',
        action: 'Buy',
        quantity: 0,
        price: 0.0,
        dollars: 0,
        currentHoldings: 0,
        restriction: getSymbolRestriction(symbolName)
      };

      // If no data exists for this row, we need to create the initial structure
      // with BOTH a default trade (what the user sees) AND the new trade
      if (!currentRowData) {
        const defaultTrade = {
          symbol: symbolName,
          account: '',
          action: 'Buy',
          quantity: 0,
          price: 0.0,
          dollars: 0,
          currentHoldings: 0,
          restriction: getSymbolRestriction(symbolName)
        };

        return {
          ...prevData,
          [rowIndex]: {
            trades: [defaultTrade, newTrade],
            resultingTarget: 0
          }
        };
      }

      // If currentRowData exists but has no trades or empty trades array,
      // we need to add the default trade that the user sees PLUS the new trade
      if (!currentRowData.trades || currentRowData.trades.length === 0) {
        const defaultTrade = {
          symbol: symbolName,
          account: '',
          action: 'Buy',
          quantity: 0,
          price: 0.0,
          dollars: 0,
          currentHoldings: 0,
          restriction: getSymbolRestriction(symbolName)
        };

        return {
          ...prevData,
          [rowIndex]: {
            ...currentRowData,
            trades: [defaultTrade, newTrade]
          }
        };
      }

      // If trades already exist, just add the new trade to the existing array
      const updatedTrades = [...currentRowData.trades, newTrade];

      return {
        ...prevData,
        [rowIndex]: {
          ...currentRowData,
          trades: updatedTrades
        }
      };
    });
  };

  // Remove a specific trade from the trades array (with race condition fix)
  const removeTradeRow = (rowIndex, tradeIndex) => {
    setTradeFormData(prevData => {
      const currentRowData = prevData[rowIndex];
      if (!currentRowData || !currentRowData.trades) return prevData;

      // Get the account of the trade being removed for cash level updates
      const removedTrade = currentRowData.trades[tradeIndex];
      const removedTradeAccount = removedTrade ? removedTrade.account : null;

      let newData;
      // If this is the only trade, reset it to default values instead of removing it
      if (currentRowData.trades.length === 1) {
        // Find the item in rebalanceData that matches this rowIndex (now rowId)
        const item = rebalanceData?.find(dataItem => {
          const itemRowId = generateRowId(dataItem, rebalanceData.indexOf(dataItem));
          return itemRowId === rowIndex;
        });
        
        // Create a default empty trade for the security
        const defaultTrade = {
          symbol: item ? String(item.Symbol || '') : '',
          account: '',
          action: 'Buy',
          quantity: 0,
          price: 0.0,
          dollars: 0,
          currentHoldings: 0
        };
        newData = {
          ...prevData,
          [rowIndex]: {
            ...currentRowData,
            trades: [defaultTrade]
          }
        };
        // Recalculate combined resulting weight with empty trades (baseline)
        calculateCombinedResultingWeightWithTrades(rowIndex, [defaultTrade]);
      } else {
        // If there are multiple trades, remove the specified one
        const updatedTrades = currentRowData.trades.filter((_, index) => index !== tradeIndex);
        newData = {
          ...prevData,
          [rowIndex]: {
            ...currentRowData,
            trades: updatedTrades
          }
        };
        // Use the race-condition-free function with the correct updated trades
        if (updatedTrades.length > 0) {
          calculateCombinedResultingWeightWithTrades(rowIndex, updatedTrades);
        } else {
          // If somehow we end up with no trades, reset to current actual weight (not target weight)
          // Find the item in rebalanceData that matches this rowIndex (now rowId)
          const item = rebalanceData?.find(dataItem => {
            const itemRowId = dataItem.Symbol || dataItem.symbol || dataItem.id || `row-${rebalanceData.indexOf(dataItem)}`;
            return itemRowId === rowIndex;
          });
          
          newData[rowIndex].resultingTarget = item ?
            (item['Actual Weight (of Assigned)'] ||
              item['Actual Weight'] ||
              item['actualWeight'] || 0) : 0;
        }
      }

      // Update cash levels for affected accounts when trade is removed/reset
      if (removedTradeAccount) {
        // Update cash levels for the account that had the trade removed
        updateAccountCashLevelsWithCurrentData(removedTradeAccount, newData);
        // Also update cash levels for any remaining accounts in the updated trades
        const remainingAccounts = new Set();
        const updatedTrades = newData[rowIndex].trades;
        updatedTrades.forEach(trade => {
          if (trade.account) {
            remainingAccounts.add(trade.account);
          }
        });
        // Update cash levels for remaining accounts (in case this was part of a multi-account trade set)
        remainingAccounts.forEach(accountName => {
          if (accountName !== removedTradeAccount) {
            updateAccountCashLevelsWithCurrentData(accountName, newData);
          }
        });
      }
      return newData;
    });
  };

  // Internal function: Get account cash status for display
  const getAccountCashStatus = (accountName) => {
    if (!accountName) return null;

    const accountNumber = accountMapping[accountName];
    if (!accountNumber) return null;

    // Check if we have updated cash info from trades
    const updatedCashInfo = accountCashLevels[accountNumber];
    if (updatedCashInfo) {
      return {
        currentCash: updatedCashInfo.new_cash_level,
        impact: updatedCashInfo.estimated_cash_impact,
        minReserve: updatedCashInfo.min_reserve,
        maxReserve: updatedCashInfo.max_reserve,
        action: updatedCashInfo.new_cash_action,
        isLoading: isLoadingCashLevels[accountNumber] || false
      };
    }

    // Fallback to cash actions data if available
    if (cashActionsData && Array.isArray(cashActionsData)) {
      const cashAction = cashActionsData.find(action =>
        action['Account Name'] === accountName || action['Account Number'] === accountNumber
      );

      if (cashAction) {
        return {
          currentCash: cashAction['Current Cash'] || cashAction['Cash Balance'] || 0,
          impact: 0,
          minReserve: cashAction['Min Reserve'] || 0,
          maxReserve: cashAction['Max Reserve'] || 0,
          action: cashAction['Cash Action'] || 'Hold',
          isLoading: false
        };
      }
    }

    return null;
  };

  // Internal function: Update account cash levels with current data
  const updateAccountCashLevelsWithCurrentData = async (accountName, currentTradeFormData) => {
    if (!accountName || !selectedGroup) return;

    const accountNumber = accountMapping[accountName];
    if (!accountNumber) return;

    const loadingKey = `${accountNumber}_cash_levels`;

    // Debounce delay
    if (cashLevelsDebounceTimers.current.has(loadingKey)) {
      clearTimeout(cashLevelsDebounceTimers.current.get(loadingKey));
    }

    const debounceTimer = setTimeout(async () => {
      if (cashLevelsLoadingLock.current.has(loadingKey)) {
        return;
      }

      if (cashLevelsAbortControllers.current.has(loadingKey)) {
        cashLevelsAbortControllers.current.get(loadingKey).abort();
        cashLevelsAbortControllers.current.delete(loadingKey);
      }

      const abortController = new AbortController();
      cashLevelsAbortControllers.current.set(loadingKey, abortController);
      cashLevelsLoadingLock.current.add(loadingKey);
      setIsLoadingCashLevels(prev => ({ ...prev, [accountNumber]: true }));

      try {
        const allTrades = [];
        const tradeData = currentTradeFormData || tradeFormData;

        Object.values(tradeData).forEach(rowData => {
          if (rowData && rowData.trades) {
            rowData.trades.forEach(trade => {
              if (trade.account === accountName && trade.symbol && trade.quantity !== 0) {
                allTrades.push({
                  account_number: accountNumber,
                  symbol: trade.symbol,
                  quantity: trade.quantity,
                  price: trade.price || 0
                });
              }
            });
          }
        });
        const response = await rrService.calculateAccountCashLevels(
          selectedGroup,
          accountNumber,
          allTrades,
          {
            signal: abortController.signal
          }
        );

        if (response) {
          setAccountCashLevels(prev => ({
            ...prev,
            [accountNumber]: {
              new_cash_level: response.new_cash_level,
              estimated_cash_impact: response.estimated_cash_impact,
              min_reserve: response.min_reserve,
              max_reserve: response.max_reserve,
              center_reserve: response.center_reserve,
              new_cash_action: response.new_cash_action,
              trades_count: response.trades_count
            }
          }));
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error updating account cash levels:', error);
        }
      } finally {
        setIsLoadingCashLevels(prev => ({ ...prev, [accountNumber]: false }));
        cashLevelsLoadingLock.current.delete(loadingKey);
        cashLevelsAbortControllers.current.delete(loadingKey);
      }
    }, 1000); // Increased debounce from 300ms to 1000ms to reduce API calls

    cashLevelsDebounceTimers.current.set(loadingKey, debounceTimer);
  };

  // Internal function: Handle trade form changes
  const handleTradeFormChange = (rowIndex, field, value, tradeIndex = 0, additionalData = {}) => {
    setTradeFormData(prevData => {
      const currentRowData = prevData[rowIndex] || { trades: [{}], resultingTarget: 0 };
      const updatedTrades = [...(currentRowData.trades || [{}])];

      if (!updatedTrades[tradeIndex]) {
        updatedTrades[tradeIndex] = {};
      }

      updatedTrades[tradeIndex] = {
        ...updatedTrades[tradeIndex],
        [field]: value
      };
      // Handle account change - clear symbol and quantity
      if (field === 'account' && value && rebalanceData) {
        // Find the item in rebalanceData that matches this rowIndex (now rowId)
        const item = rebalanceData.find(dataItem => {
          const itemRowId = generateRowId(dataItem, rebalanceData.indexOf(dataItem));
          return itemRowId === rowIndex;
        });
        
        if (item) {
          updatedTrades[tradeIndex].symbol = '';
          updatedTrades[tradeIndex].quantity = 0;
          updatedTrades[tradeIndex].price = 0.0;
          updatedTrades[tradeIndex].dollars = 0;
          updatedTrades[tradeIndex].currentHoldings = 0;

          // Note: Symbols are already loaded when the row was expanded
          // No need to load symbols here since they're preloaded for all accounts
        }
      }

      // If symbol changed, add restriction and price, then load holdings
      if (field === 'symbol' && value) {
        // Use restriction from additionalData (from symbol dropdown) if available, otherwise fallback to getSymbolRestriction
        const restriction = additionalData.restriction !== undefined ? additionalData.restriction : getSymbolRestriction(value);
        updatedTrades[tradeIndex].restriction = restriction;

        // Use price from additionalData (from symbol dropdown) if available
        if (additionalData.price !== undefined) {
          updatedTrades[tradeIndex].price = additionalData.price;
          // Also update dollars value when price changes
          const quantity = updatedTrades[tradeIndex].quantity || 0;
          updatedTrades[tradeIndex].dollars = quantity * additionalData.price;
        }

        if (updatedTrades[tradeIndex].account) {
          const accountNumber = accountMapping[updatedTrades[tradeIndex].account];

          if (accountNumber) {
            loadAccountHoldings(rowIndex, accountNumber, value, tradeIndex);

            // Fallback: Inject cached price from security level model data if not provided in additionalData
            if (additionalData.price === undefined && rebalanceData) {
              // Find the item in rebalanceData that matches this rowIndex (now rowId)
              const item = rebalanceData.find(dataItem => {
                const itemRowId = generateRowId(dataItem, rebalanceData.indexOf(dataItem));
                return itemRowId === rowIndex;
              });
              
              if (item) {
                const modelName = item['Security Level Model Name'];
                const accountName = updatedTrades[tradeIndex].account;

                const cachedPrice = getSymbolPrice(value, modelName, accountName);
                if (cachedPrice !== null) {
                  updatedTrades[tradeIndex].price = cachedPrice;
                  // Also update dollars value when price changes
                  const quantity = updatedTrades[tradeIndex].quantity || 0;
                  updatedTrades[tradeIndex].dollars = quantity * cachedPrice;
                }
              }
            }
          }
        }
      }

      // Update restriction when quantity changes (to trigger restriction warnings)
      if (field === 'quantity' && updatedTrades[tradeIndex].symbol) {
        // Use existing restriction from trade if available, otherwise fallback to getSymbolRestriction
        const existingRestriction = updatedTrades[tradeIndex].restriction;
        const restriction = existingRestriction !== undefined ? existingRestriction : getSymbolRestriction(updatedTrades[tradeIndex].symbol);
        updatedTrades[tradeIndex].restriction = restriction;
      }

      const newData = {
        ...prevData,
        [rowIndex]: {
          ...currentRowData,
          trades: updatedTrades
        }
      };

      // Calculate combined resulting weight with the updated trades
      if (field === 'quantity' || field === 'symbol') {
        const validTrades = updatedTrades.filter(trade =>
          trade.account && trade.symbol && trade.quantity !== 0
        );
        calculateCombinedResultingWeightWithTrades(rowIndex, validTrades);
      }

      // Update cash levels for only the affected account(s)
      if (field === 'account') {
        // For account change, update both old and new account if different
        const oldAccount = currentRowData.trades && currentRowData.trades[tradeIndex] ? currentRowData.trades[tradeIndex].account : undefined;
        const newAccount = value;
        if (oldAccount && oldAccount !== newAccount) {
          updateAccountCashLevelsWithCurrentData(oldAccount, newData);
        }
        if (newAccount) {
          updateAccountCashLevelsWithCurrentData(newAccount, newData);
        }
      } else if (field === 'quantity' || field === 'symbol') {
        // For quantity or symbol change, update only the account on the edited trade
        const currentAccount = updatedTrades[tradeIndex]?.account;
        if (currentAccount) {
          updateAccountCashLevelsWithCurrentData(currentAccount, newData);
        }
      }

      return newData;
    });
  };

  // Internal function: Load account holdings for a symbol
  const loadAccountHoldings = async (rowIndex, accountNumber, symbol, tradeIndex = 0) => {
    if (!accountNumber || !symbol || !selectedGroup) return;

    try {
      const response = await rrService.getAccountHoldings(
        accountNumber,
        symbol,
        {}
      );

      if (response && response.holdings && Array.isArray(response.holdings)) {
        const holdingsMap = {};
        response.holdings.forEach(holding => {
          const holdingSymbol = holding.Symbol || holding.symbol;
          const quantity = parseFloat(holding.Quantity || holding.quantity || 0);
          if (holdingSymbol) {
            holdingsMap[holdingSymbol] = quantity;
          }
        });

        // Update the trade form data with current holdings for this symbol
        const currentHoldings = holdingsMap[symbol] || 0;
        setTradeFormData(prevData => {
          const currentRowData = prevData[rowIndex];
          const updatedTrades = [...(currentRowData && currentRowData.trades ? currentRowData.trades : [{}])];
          if (!updatedTrades[tradeIndex]) {
            updatedTrades[tradeIndex] = {};
          }
          updatedTrades[tradeIndex] = {
            ...updatedTrades[tradeIndex],
            currentHoldings: currentHoldings
          };
          return {
            ...prevData,
            [rowIndex]: {
              ...currentRowData,
              trades: updatedTrades
            }
          };
        });
      }
    } catch (error) {
      console.error('Error loading account holdings:', error);
    }
  };

  // Internal function: Get symbol price from cached security level model data
  const getSymbolPrice = (symbol, modelName, accountName) => {
    if (!symbol || !modelName) return null;
    const accountNumber = accountMapping[accountName] || 'all';

    // Primary: Check symbolsForModelsAccounts first (new efficient data structure)
    if (symbolsForModelsAccounts &&
      symbolsForModelsAccounts[accountNumber] &&
      symbolsForModelsAccounts[accountNumber][modelName]) {
      const symbolData = symbolsForModelsAccounts[accountNumber][modelName];
      const symbolEntry = symbolData.find(item =>
        (item.Symbol === symbol || item.symbol === symbol)
      );
      if (symbolEntry) {
        return parseFloat(symbolEntry.Price || symbolEntry.price || 0);
      }
    }


    return null;
  };

  // Memoize the render function to prevent unnecessary re-renders
  const renderTradeFormLocal = useCallback((item, rowId) => {
    // For UNASSIGNED CASH, render directly since React.memo will handle optimization
    const modelName = item['Security Level Model Name'];
    if (isModelUnassignedCash(modelName)) {
      return (
        <UnassignedCashView
          key={`cash-${rowId}`}
          item={item}
          rowId={rowId}
          accountMapping={accountMapping}
          hideDollarValues={hideDollarValues}
          formatCurrency={formatCurrency}
          symbolsForModelsAccounts={symbolsForModelsAccounts}
        />
      );
    }

    // For TradeForm, don't cache since it has dynamic state that needs updates
    const mergedSymbols = {
      ...localSecurityLevelModelSymbols
    };

    return (
      <TradeForm
        key={`trade-${rowId}`}
        item={item}
        rowIndex={rowId}
        tradeFormData={tradeFormData}
        onTradeFormChange={handleTradeFormChange}
        availableAccounts={availableAccounts}
        accountMapping={accountMapping}
        securityLevelModelSymbols={mergedSymbols}
        loadingSymbolsFor={loadingSymbolsFor}
        symbolHoldings={{}}
        getAccountCashStatus={getAccountCashStatus}
        accountCashLevels={accountCashLevels}
        isLoadingCashLevels={isLoadingCashLevels}
        formatCurrency={formatCurrency}
        getSymbolRestriction={getSymbolRestriction}
        addTradeRow={addTradeRow}
        removeTradeRow={removeTradeRow}
        symbolsForModelsAccounts={symbolsForModelsAccounts}
      />
    );
  }, [
    hideDollarValues,
    accountMapping,
    localSecurityLevelModelSymbols,
    availableAccounts,
    formatCurrency,
    tradeFormData,
    handleTradeFormChange,
    loadingSymbolsFor,
    getAccountCashStatus,
    accountCashLevels,
    isLoadingCashLevels,
    getSymbolRestriction,
    addTradeRow,
    removeTradeRow,
    symbolsForModelsAccounts
  ]);
  // Apply trades from rebalance result to the main trade form
  const applyRebalanceTrades = async () => {
    if (!rebalanceResult || !rebalanceResult.trades || !rebalanceData) {
      console.warn('No rebalance result or trade data available to apply');
      alert('No rebalance result or trade data available to apply');
      return;
    }

    // Close the modal first
    setShowRebalanceModal(false);

    // Process the trades and prepare the trade form data using already loaded symbols
    const newTradeFormData = { ...tradeFormData };
    const rowsToExpand = new Set();

    // Group trades by symbol for better organization
    const tradesBySymbol = {};
    rebalanceResult.trades.forEach(trade => {
      const symbol = trade.symbol || '';
      if (!tradesBySymbol[symbol]) {
        tradesBySymbol[symbol] = [];
      }
      tradesBySymbol[symbol].push(trade);
    });

    // For each row in the rebalance data, find corresponding trades
    rebalanceData.forEach((row, rowIndex) => {
      const symbolsString = String(row.Symbol || '');
      // Split comma-separated symbols and trim whitespace
      const symbolList = symbolsString.split(',').map(s => s.trim()).filter(Boolean);

      // Find all trades that match any symbol in this row
      let rowTrades = [];
      symbolList.forEach(symbol => {
        if (tradesBySymbol[symbol]) {
          rowTrades = rowTrades.concat(tradesBySymbol[symbol]);
        }
      });

      if (rowTrades.length > 0) {
        // Create stable row identifier based on Security Level Model Name
        const rowId = generateRowId(row, rowIndex);
        // Mark this row for expansion
        rowsToExpand.add(rowId);

        // Convert rebalance trades to trade form format
        const convertedTrades = rowTrades.map(trade => {
          // Get the account number from the trade
          const tradeAccountNumber = trade.account_number || trade.accountNumber || '';

          // Find the account name that matches this account number
          let matchedAccount = '';
          if (tradeAccountNumber) {
            // Find the account name that maps to this account number
            const accountEntry = Object.entries(accountMapping).find(([name, number]) =>
              number === tradeAccountNumber
            );

            if (accountEntry) {
              matchedAccount = accountEntry[0]; // The account name
            } else {
              console.warn(`Debug: No account name found for account number "${tradeAccountNumber}" in account mapping:`, accountMapping);
            }
          } else {
            console.warn('Debug: No account number found in trade:', trade);
          }

          // For symbol, we need to make sure it's one of the symbols in the row's symbol list
          let tradeSymbol = trade.symbol || '';

          // If the trade symbol isn't in the row's symbol list, try to find a match
          if (!symbolList.includes(tradeSymbol) && symbolList.length > 0) {
            // Use the first symbol in the list as a fallback
            tradeSymbol = symbolList[0];
          }

          // Get current holdings from the preloaded symbolsForModelsAccounts data
          let currentHoldings = 0;
          let symbolPrice = 0;

          // Look for symbol data in symbolsForModelsAccounts
          if (symbolsForModelsAccounts &&
            symbolsForModelsAccounts[tradeAccountNumber] &&
            symbolsForModelsAccounts[tradeAccountNumber][trade.security_model]) {
            const symbolData = symbolsForModelsAccounts[tradeAccountNumber][trade.security_model];
            const symbolItem = symbolData.find(item => {
              const symbol = item.Symbol || item.symbol;
              return symbol === tradeSymbol;
            });

            if (symbolItem) {
              currentHoldings = parseFloat(symbolItem.Quantity || symbolItem.quantity || 0);
              symbolPrice = parseFloat(symbolItem.Price || symbolItem.price || 0);
            }
          }

          const tradeObject = {
            symbol: tradeSymbol,
            account: matchedAccount,
            action: trade.action === 'BUY' ? 'Buy' : trade.action === 'SELL' ? 'Sell' : 'Buy',
            quantity: trade.action === 'SELL' ? -Math.abs(parseFloat(trade.quantity || 0)) : Math.abs(parseFloat(trade.quantity || 0)),
            price: symbolPrice || parseFloat(trade.price || 0),
            dollars: trade.action === 'SELL' ? -Math.abs(parseFloat(trade.value || 0)) : Math.abs(parseFloat(trade.value || 0)),
            currentHoldings: currentHoldings,
            restriction: getSymbolRestriction(tradeSymbol)
          };

          return tradeObject;
        });

        // Add the trades to the trade form data
        newTradeFormData[rowId] = {
          trades: convertedTrades,
          resultingTarget: newTradeFormData[rowId]?.resultingTarget || 0
        };
      }
    });

    // Update the trade form data
    setTradeFormData(newTradeFormData);

    // Use setTimeout to ensure trade form data is updated before expanding rows and updating calculations
    setTimeout(() => {
      // Expand all rows that have trades
      setExpandedTradeRows(new Set(rowsToExpand));

      // Collect all security model trades for bulk API call
      const securityModelTrades = [];
      const affectedAccounts = new Set();

      Object.entries(newTradeFormData).forEach(([rowIndex, formData]) => {
        if (formData.trades && formData.trades.length > 0) {
          const validTrades = formData.trades.filter(trade =>
            trade.account && trade.symbol && trade.quantity !== 0
          );

          if (validTrades.length > 0) {
            // Get security level model for this row
            let securityLevelModel = undefined;
            // Find the item in rebalanceData that matches this rowIndex (now rowId)
            // The rowIndex here is the rowId that was created during trade application
            const item = rebalanceData?.find(dataItem => {
              // Use the same row ID generation logic
              const itemRowId = generateRowId(dataItem, rebalanceData.indexOf(dataItem));
              return itemRowId === rowIndex;
            });
            
            if (item) {
              const modelNameData = item['Security Level Model Name'];
              if (typeof modelNameData === 'object' && modelNameData !== null && modelNameData.value !== undefined) {
                securityLevelModel = String(modelNameData.value);
              } else if (modelNameData !== undefined && modelNameData !== null) {
                securityLevelModel = String(modelNameData);
              }
            }

            if (securityLevelModel && securityLevelModel.trim() !== '') {
              securityModelTrades.push({
                rowIndex: rowIndex,
                securityLevelModel: securityLevelModel,
                trades: validTrades
              });
            }

            // Collect affected accounts for cash level updates
            validTrades.forEach(trade => {
              if (trade.account) {
                affectedAccounts.add(trade.account);
              }
            });
          }
        }
      });

      // Make bulk API call for combined resulting weights
      if (securityModelTrades.length > 0) {
        calculateCombinedResultingWeightBulk(securityModelTrades);
      }

      // Update cash levels for affected accounts with staggered timing
      let accountIndex = 0;
      affectedAccounts.forEach(accountName => {
        setTimeout(() => {
          updateAccountCashLevelsWithCurrentData(accountName, newTradeFormData);
        }, accountIndex * 300);
        accountIndex++;
      });
    }, 200);
  };

  return (
    <>
      <div className={`collapse ${!isRebalanceCollapsed ? 'show' : ''}`}>
        <div className="card-body">
          {/* Portfolio Rebalance and Raise Cash Buttons */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h6 className="mb-0">Generate Portfolio Actions</h6>
            <div className="d-flex gap-2">
              <button
                className="btn btn-success"
                onClick={handlePortfolioRebalance}
                disabled={!selectedGroup || isLoadingPortfolioRebalance}
                title="Generate portfolio rebalance recommendations"
              >
                {isLoadingPortfolioRebalance ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Processing...
                  </>
                ) : (
                  'Portfolio Rebalance'
                )}
              </button>
              <button
                className="btn btn-warning"
                onClick={handleSpringCleaning}
                disabled={!selectedGroup || isLoadingPortfolioRebalance}
                title="Sell everything and rebuild portfolio with optimal tax placement"
              >
                {isLoadingPortfolioRebalance ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Processing...
                  </>
                ) : (
                  'Spring Cleaning'
                )}
              </button>
              <button
                className="btn btn-info"
                onClick={handleRaiseCash}
                disabled={!selectedGroup || isLoadingPortfolioRebalance}
                title="Generate cash raising recommendations"
              >
                {isLoadingPortfolioRebalance ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Processing...
                  </>
                ) : (
                  'Raise Cash'
                )}
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={handleClearAllTrades}
                disabled={Object.keys(tradeFormData || {}).length === 0}
                title="Clear all trades and collapse all rows"
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear All Trades
              </button>
            </div>
          </div>
          {/* Rebalance Guide - Collapsible */}
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card h-100 bg-light">
                <div
                  className="card-header bg-light d-flex justify-content-between align-items-center py-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsRebalanceGuideCollapsed(!isRebalanceGuideCollapsed)}
                >
                  <h6 className="mb-0">
                    <i className={`bi ${isRebalanceGuideCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                    {' '}Rebalance Guide
                  </h6>
                </div>
                <div className={`collapse ${!isRebalanceGuideCollapsed ? 'show' : ''}`}>
                  <div className="card-body py-2">
                    <div className="small">
                      <p className="mb-2">This report shows securities with details about holdings, allocations, and trading recommendations across all accounts in the selected group for rebalancing purposes.</p>
                      <h6 className="mb-2 text-primary">Trade Action with Restriction Color Guide:</h6>
                      <div className="d-flex flex-wrap gap-3 mb-3">
                        <div><span style={{ ...getTradeActionStyles('trade-action-buy'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>BUY</span> - Pure buy action (green)</div>
                        <div><span style={{ ...getTradeActionStyles('trade-action-sell'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>SELL</span> - Pure sell action (red)</div>
                        <div><span style={{ ...getTradeActionStyles('trade-action-hold'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>HOLD</span> - Hold action (gray)</div>
                      </div>

                      <div className="d-flex flex-wrap gap-3 mb-3">
                        <div><span style={{ ...getTradeActionStyles('trade-action-buy-weight'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>BUY within Weight</span> - Buy with weight restrictions (light green)</div>
                        <div><span style={{ ...getTradeActionStyles('trade-action-sell-weight'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>SELL within Weight</span> - Sell with weight restrictions (light red)</div>
                      </div>
                      <div className="d-flex flex-wrap gap-3 mb-3">
                        <div><span style={{ ...getTradeActionStyles('trade-action-conflict'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>BUY/SELL but (...restriction)</span> - Conflicting restrictions (orange)</div>
                        <div><span style={{ ...getTradeActionStyles('trade-action-unknown'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>Unknown</span> - Unknown actions (light gray)</div>
                      </div>

                      <h6 className="mb-2 text-primary">Trade Action Values:</h6>
                      <div className="small text-muted">
                        <p className="mb-1">• <strong>Pure Actions:</strong> "BUY", "SELL", "HOLD" - Simple trade recommendations based on target weights</p>
                        <p className="mb-1">• <strong>Weight Restrictions:</strong> "BUY within Weight (X% - Y%)" or "SELL within Weight (X% - Y%)" - Actions with percentage constraints by the client, not the model</p>
                        <p className="mb-1">• <strong>Conflicting Restrictions:</strong> "BUY but (SELL ONLY restriction)" - When model recommendation conflicts with client's or global restrictions</p>
                        <p className="mb-0">• <strong>Empty/Null:</strong> No action required, typically for cash positions or unassigned securities</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rebalance Data Loading State */}
          {isLoadingRebalance ? (
            <div className="d-flex justify-content-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading rebalance information...</span>
              </div>
              <span className="ms-2">Loading rebalance information...</span>
            </div>
          ) : rebalanceError ? (
            <div className="alert alert-danger">
              <strong>Error:</strong> {rebalanceError}
            </div>
          ) : rebalanceData ? (
            <div className="mt-4">
              {/* Layout with Table on left and Cash Summary on right */}
              <div className="row">
                <div className="col-lg-10">
                  <SortableResizableTable
                    columns={securitiesReportColumns}
                    data={rebalanceData}
                    hideDollarValues={hideDollarValues}
                    customRender={getTableConfig(rebalanceData).customRender}
                    expandableRows={true}
                    expandedRows={expandedTradeRows}
                    onRowExpand={handleRowExpand}
                    generateRowId={generateRowId}
                    columnClickHandlers={{
                      'Security Level Model Name': handleSecurityLevelModelClick
                    }}
                    renderExpandedContent={renderTradeFormLocal}
                    tradeFormData={tradeFormData}
                    onTradeFormChange={handleTradeFormChange}
                  />
                </div>
                <div className="col-lg-2">
                  <div style={{ position: 'sticky', top: '20px', zIndex: 1000 }}>
                    {/* Current Cash Summary with Real-Time Updates - Right of the Rebalance Table */}
                    <CashSummaryPanel
                      cashActionsData={cashActionsData}
                      isLoadingRebalance={isLoadingRebalance}
                      rebalanceError={rebalanceError}
                      accountCashLevels={accountCashLevels}
                      accountMapping={accountMapping}
                      isLoadingCashLevels={isLoadingCashLevels}
                      isLoadingCashActions={isLoadingCashActions}
                      formatCurrency={formatCurrency}
                    />
                  </div>
                </div>
              </div>
              {/* Generate Order Form Button */}
              <div className="mt-4 d-flex justify-content-center">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => setShowOrderFormDialog(true)}
                  disabled={Object.keys(tradeFormData).length === 0}
                >
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Generate Order Form
                </button>
              </div>
            </div>
          ) : null}
          {/* Order Form Dialog */}
          <OrderFormDialog
            isOpen={showOrderFormDialog}
            onClose={() => setShowOrderFormDialog(false)}
            trades={collectAllTrades()}
            issues={analyzeTradesForIssues(collectAllTrades()).issues}
            warnings={analyzeTradesForIssues(collectAllTrades()).warnings}
            formatCurrency={formatCurrency}
            selectedGroup={selectedGroup}
            accountMapping={accountMapping}
            accountCustodianMapping={accountCustodianMapping}
          />
        </div>
      </div>      {/* Portfolio Rebalance Results Modal */}
      {showRebalanceModal && (
        <PortfolioRebalanceResultsModal
          show={showRebalanceModal}
          onClose={() => setShowRebalanceModal(false)}
          isLoadingPortfolioRebalance={isLoadingPortfolioRebalance}
          portfolioRebalanceError={portfolioRebalanceError}
          rebalanceResult={rebalanceResult}
          applyRebalanceTrades={applyRebalanceTrades}
          operationType={rebalanceOperationType}
          hideDollarValues={hideDollarValues}
        />
      )}
    </>
  );
};

export default RebalancePanel;
